import { useParams, Link } from 'react-router-dom';
import SEOHead from '@/components/feature/SEOHead';
import SchemaOrg from '@/components/base/SchemaOrg';
import Breadcrumb from '@/components/feature/Breadcrumb';
import { categories, allCategorySlugs } from '@/mocks/categories';
import { getArticlesByCategory } from '@/mocks/articles';

export default function Category() {
  const { slug } = useParams<{ slug: string }>();
  const currentSlug = slug || 'brand-story';
  const currentCategory = categories.find((c) => c.slug === currentSlug) || categories[0];
  const categoryArticles = getArticlesByCategory(currentSlug);

  return (
    <>
      <SEOHead
        title={`${currentCategory.name}——高柳家 | 官方品牌网站`}
        description={currentCategory.description}
        keywords={`高柳家,${currentCategory.name},高柳家文化,高柳家特色`}
        canonicalUrl={`https://takayanagi-family.com/category/${currentSlug}`}
        ogTitle={`${currentCategory.name}——高柳家`}
        ogDescription={currentCategory.description}
      />
      <SchemaOrg type="WebPage" data={{
        name: `${currentCategory.name}——高柳家`,
        url: `https://takayanagi-family.com/category/${currentSlug}`,
        description: currentCategory.description,
        inLanguage: 'zh-Hans',
        isPartOf: { '@type': 'WebSite', name: '高柳家', url: 'https://takayanagi-family.com' },
      }} />

      <main className="pt-16">
        {/* Category Header */}
        <section className="relative w-full h-[280px] md:h-[360px] flex items-center justify-center overflow-hidden">
          <img
            src={currentCategory.image}
            alt={`高柳家${currentCategory.name}`}
            title={`高柳家——${currentCategory.name}`}
            className="absolute inset-0 w-full h-full object-cover object-top"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-black/30 via-black/20 to-black/30"></div>
          <div className="relative z-10 text-center px-4">
            <div className="w-14 h-14 mx-auto flex items-center justify-center rounded-full bg-white/20 text-white">
              <i className={`${currentCategory.icon} text-2xl`}></i>
            </div>
            <h1 className="mt-4 text-2xl md:text-4xl font-heading font-semibold text-white">{currentCategory.name}</h1>
            <p className="mt-3 text-sm md:text-base text-white/80 max-w-xl mx-auto">{currentCategory.description}</p>
          </div>
        </section>

        {/* Breadcrumb */}
        <div className="max-w-7xl mx-auto px-4 md:px-6">
          <Breadcrumb
            items={[
              { label: '首页', href: '/' },
              { label: currentCategory.name },
            ]}
          />
        </div>

        {/* Category Tabs */}
        <section className="pb-4 bg-background-50">
          <div className="max-w-7xl mx-auto px-4 md:px-6">
            <div className="flex flex-wrap gap-2">
              {categories.map((cat) => (
                <Link
                  key={cat.slug}
                  to={`/category/${cat.slug}`}
                  className={`whitespace-nowrap inline-flex items-center gap-1.5 px-4 py-2 rounded-full text-sm font-medium transition-all duration-200 ${
                    currentSlug === cat.slug
                      ? 'bg-primary-500 text-background-50 dark:text-foreground-950'
                      : 'bg-background-100 text-foreground-600 hover:bg-background-200'
                  }`}
                >
                  <div className="w-4 h-4 flex items-center justify-center">
                    <i className={`${cat.icon} text-xs`}></i>
                  </div>
                  {cat.name}
                </Link>
              ))}
            </div>
          </div>
        </section>

        {/* Articles List */}
        <section className="py-10 md:py-14 bg-background-50">
          <div className="max-w-7xl mx-auto px-4 md:px-6">
            {categoryArticles.length > 0 ? (
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6 md:gap-8">
                {categoryArticles.map((article) => (
                  <Link
                    key={article.id}
                    to={`/article/${article.id}`}
                    className="group block rounded-lg overflow-hidden bg-background-100 transition-all duration-300 hover:scale-[1.02]"
                  >
                    <div className="relative h-48 overflow-hidden">
                      <img
                        src={article.image}
                        alt={article.title}
                        title={article.title}
                        className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-500"
                      />
                    </div>
                    <div className="p-4 md:p-5">
                      <h3 className="text-base font-heading font-semibold text-foreground-900 line-clamp-2 leading-snug group-hover:text-primary-600 transition-colors duration-200">
                        {article.title}
                      </h3>
                      <p className="mt-2 text-sm text-foreground-500 line-clamp-2 leading-relaxed">{article.excerpt}</p>
                      <div className="flex items-center gap-3 mt-3 text-xs text-foreground-400">
                        <span>{article.date}</span>
                        <span>{article.readTime}</span>
                      </div>
                    </div>
                  </Link>
                ))}
              </div>
            ) : (
              <div className="text-center py-16">
                <div className="w-16 h-16 mx-auto flex items-center justify-center rounded-full bg-background-100 text-foreground-400">
                  <i className="ri-article-line text-2xl"></i>
                </div>
                <p className="mt-4 text-foreground-500">该分类暂无文章</p>
              </div>
            )}
          </div>
        </section>
      </main>
    </>
  );
}